import React, { useState } from 'react';
import { Share2, Send, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { api } from '../utils/api';

// Share-by-email dialog on the Organic design system. Collects a
// recipient email, an optional recipient name and an optional note,
// then POSTs to /api/brands/{id}/share, which emails the brand
// intelligence report via SendGrid. The name lands on the recipient's
// follower row so Following can show a person instead of an address.
// Opened with a `brand` from the brands detail pane, the brand detail
// page and the Following cards; the Following page's header "Share a
// brand" button opens it without one and passes `brandOptions` so the
// operator picks the brand here instead.

const STROKE = 2.75;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

function ShareBrandModal({ brand, brandOptions, onClose, onShared }) {
  const [brandId, setBrandId] = useState(brand ? brand.id : '');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [phase, setPhase] = useState('form'); // form | sending | done | error
  const [error, setError] = useState(null);
  const [sentTo, setSentTo] = useState(null);

  const pickBrand = !brand;
  const target = brand
    || (brandOptions || []).find((b) => String(b.id) === String(brandId))
    || null;
  const emailValid = EMAIL_RE.test(email.trim());
  const canSend = emailValid && !!target;

  const send = async () => {
    if (!canSend || phase === 'sending') return;
    setPhase('sending');
    setError(null);
    try {
      const result = await api.shareBrand(target.id, email.trim(), note.trim(), name.trim());
      setSentTo(email.trim());
      setPhase('done');
      // Report is now shared — the recipient was recorded as a follower.
      // Hand the refreshed brand back so follower lists update without
      // a full page reload.
      if (onShared) onShared(result?.brand || null);
    } catch (err) {
      setError(err.message || 'Failed to send the report.');
      setPhase('error');
    }
  };

  const reset = () => {
    setEmail('');
    setName('');
    setNote('');
    setError(null);
    setPhase('form');
  };

  const onEnter = (e) => { if (e.key === 'Enter') send(); };

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="dialog" style={{ width: 'min(460px, 100%)' }} onClick={(e) => e.stopPropagation()}>
        <div className="dialog-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <Share2 size={18} strokeWidth={STROKE} /> Share {target ? target.name : 'a brand'}
        </div>

        {phase === 'done' ? (
          <>
            <p
              style={{
                margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 14,
                padding: 'var(--space-3)', borderRadius: 'var(--radius-md)',
                background: 'var(--color-accent-2-100)', color: 'var(--color-accent-2-800)',
              }}
            >
              <CheckCircle2 size={18} strokeWidth={STROKE} style={{ flex: 'none' }} />
              <span>Report for {target.name} sent to <strong>{sentTo}</strong>.</span>
            </p>
            <div className="dialog-actions">
              <button type="button" className="btn btn-secondary" onClick={reset}>Share with someone else</button>
              <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <>
            <div className="dialog-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <p style={{ margin: 0, fontSize: 13, opacity: 0.75, textWrap: 'pretty' }}>
                Emails the brand intelligence report &mdash; profile, links,
                latest metrics and any discovered contacts &mdash; and adds
                the recipient as a follower, so the brand lands on your
                Following page.
              </p>

              {phase === 'error' && (
                <p
                  style={{
                    margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
                    padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)',
                    background: 'var(--color-accent-100)', color: 'var(--color-accent-800)',
                  }}
                >
                  <AlertCircle size={16} strokeWidth={STROKE} style={{ flex: 'none' }} /> {error}
                </p>
              )}

              {pickBrand && (
                <div className="field">
                  <label>Brand</label>
                  <select
                    className="input"
                    value={brandId}
                    onChange={(e) => setBrandId(e.target.value)}
                    disabled={phase === 'sending' || !brandOptions}
                    autoFocus
                  >
                    <option value="" disabled>
                      {brandOptions ? 'Pick a brand…' : 'Loading brands…'}
                    </option>
                    {(brandOptions || []).map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="field">
                <label>Recipient email</label>
                <input
                  className="input"
                  type="email"
                  placeholder="colleague@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={onEnter}
                  autoFocus={!pickBrand}
                  disabled={phase === 'sending'}
                />
              </div>
              <div className="field">
                <label>Recipient name <span style={{ opacity: 0.6 }}>optional</span></label>
                <input
                  className="input"
                  placeholder="Jane Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={onEnter}
                  disabled={phase === 'sending'}
                />
              </div>
              <div className="field">
                <label>Note <span style={{ opacity: 0.6 }}>optional</span></label>
                <textarea
                  className="input"
                  rows={3}
                  placeholder="Why this brand is worth a look…"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={phase === 'sending'}
                />
              </div>
            </div>

            <div className="dialog-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={phase === 'sending'}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" disabled={!canSend || phase === 'sending'} onClick={send}>
                {phase === 'sending'
                  ? (<><Loader2 size={15} strokeWidth={STROKE} className="spin" /> Sending…</>)
                  : (<><Send size={15} strokeWidth={STROKE} /> Send report</>)}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ShareBrandModal;
